import React, {useEffect, useState, useContext, useRef} from 'react';
import styles from './QuizModal.module.css';
import ReactDOM from 'react-dom';
import closeButtonHoverImg from '../../static_files/closeButtonHover.svg';
import closeButtonImg from "../../static_files/closeButton.svg";
import goldBg from '../../static_files/buttonBackgroundGold.svg';
import pointIcon from '../../static_files/point icon.svg';
import checkCorrectIcon from '../../static_files/check-correct.svg';
import checkWrongIcon from '../../static_files/check-wrong.svg';

import {SwapMainAppContext} from "../../context/SwapMainAppContext";
import {PRISMAX_BACKEND_URL} from "../../configs/config";
import {qd} from "../../utils/xlz";
import {useRecaptcha} from "../../utils/useRecaptcha";


function QuizModal({onClose, hasCompletedQuiz, quizPoints}) {
    const {userId, showNotification} = useContext(SwapMainAppContext);
    const {executeRecaptchaAction, isRecaptchaReady} = useRecaptcha();

    const [closeButtonHover, setCloseButtonHover] = useState(false);
    const [currentView, setCurrentView] = useState(hasCompletedQuiz ? 'results' : 'start'); // 'start', 'quiz', 'complete', 'results'
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [hoveredOption, setHoveredOption] = useState(null);
    const [earnedPoints, setEarnedPoints] = useState(quizPoints || 0);
    const [isLoading, setIsLoading] = useState(false);
    const [isReviewMode] = useState(hasCompletedQuiz); // Track if we're in review mode

    // Confetti animation refs
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const particlesRef = useRef([]);
    const modalContainerRef = useRef(null);
    const [randomizedQuestions, setRandomizedQuestions] = useState([]);

    const shuffleArray = (array) => {
        const newArray = [...array];
        for (let i = newArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
        }
        return newArray;
    };
    useEffect(() => {
        const shuffledQuestions = shuffleArray(qd.map(q => ({
            ...q,
            options: [...q.options] // Keep original order of options (A, B, C, D)
        })));
        setRandomizedQuestions(shuffledQuestions);
    }, []);


    // Confetti effect for high scores
    useEffect(() => {
        if (currentView !== 'complete' || earnedPoints < 500) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#FFD93D', '#6BCF7F', '#E056FD'];

        class Confetti {
            constructor() {
                this.x = Math.random() * canvas.width;
                this.y = Math.random() * canvas.height - canvas.height;
                this.w = Math.random() * 10 + 5;
                this.h = Math.random() * 5 + 3;
                this.color = colors[Math.floor(Math.random() * colors.length)];
                this.speed = Math.random() * 3 + 2;
                this.velX = Math.random() * 2 - 1;
                this.angle = Math.random() * 360;
                this.angleSpeed = Math.random() * 0.2 - 0.1;
                this.opacity = 1;
                this.isDead = false;
            }

            update() {
                this.y += this.speed;
                this.x += this.velX;
                this.angle += this.angleSpeed;

                if (this.y > canvas.height * 0.7) {
                    this.opacity = Math.max(0, 1 - (this.y - canvas.height * 0.7) / (canvas.height * 0.3));
                }

                // Only mark as dead when completely off screen (added extra margin)
                if (this.y > canvas.height + this.h + 20) {
                    this.isDead = true;
                }
            }

            draw(ctx) {
                ctx.save();
                ctx.translate(this.x + this.w / 2, this.y + this.h / 2);
                ctx.rotate(this.angle);
                ctx.globalAlpha = this.opacity;
                ctx.fillStyle = this.color;
                ctx.fillRect(-this.w / 2, -this.h / 2, this.w, this.h);
                ctx.restore();
            }
        }

        particlesRef.current = [];
        for (let i = 0; i < 150; i++) {
            particlesRef.current.push(new Confetti());
        }

        let startTime = Date.now();
        const duration = 12000; // Extended from 8000 to 12000

        function animate() {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const elapsed = Date.now() - startTime;

            particlesRef.current.forEach(particle => {
                particle.update();
                particle.draw(ctx);
            });

            particlesRef.current = particlesRef.current.filter(particle => !particle.isDead);

            if (elapsed < 3000 && particlesRef.current.length < 200) {
                for (let i = 0; i < 2; i++) {
                    particlesRef.current.push(new Confetti());
                }
            }

            // Continue animation until all particles have fallen
            if (particlesRef.current.length > 0 || elapsed < duration) {
                animationRef.current = requestAnimationFrame(animate);
            }
        }

        animate();

        const handleResize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        window.addEventListener('resize', handleResize);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            window.removeEventListener('resize', handleResize);
            particlesRef.current = [];
        };
    }, [currentView, earnedPoints]);

    useEffect(() => {
        if (window.innerHeight < 800 && currentView === 'quiz' && modalContainerRef.current) {
            setTimeout(() => {
                modalContainerRef.current.scrollTo({
                    top: modalContainerRef.current.scrollHeight,
                    behavior: 'auto' // Changed from 'smooth' to 'auto' for instant scroll
                });
            }, 1);
        }
    }, [currentView, currentQuestion]);

    // Calculate earned points
    const calculatePoints = () => {
        let correctCount = 0;
        Object.entries(selectedAnswers).forEach(([questionIndex, selectedOption]) => {
            const question = randomizedQuestions[parseInt(questionIndex)];
            const selectedAnswer = question.options.find(opt => opt.id === selectedOption);
            if (selectedAnswer && selectedAnswer.correct) {
                correctCount++;
            }
        });
        let totalPoints
        if (correctCount === randomizedQuestions.length) {
            totalPoints = correctCount * 500 + 1000
        } else {
            totalPoints = correctCount * 500
        }
        return totalPoints;
    };

    // Handle answer selection
    const handleAnswerSelect = (optionId) => {
        setSelectedAnswers({
            ...selectedAnswers,
            [currentQuestion]: optionId
        });
    };

    // Handle navigation
    const handleNext = () => {
        if (currentQuestion < qd.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
        }
    };

    const handleBack = () => {
        if (currentQuestion > 0) {
            setCurrentQuestion(currentQuestion - 1);
        }
    };

    const handleFinishQuiz = async () => {
        if (!userId) {
            showNotification('error', 'Authentication required. Please log in.');
            return;
        }

        if (!isRecaptchaReady) {
            showNotification('error', 'Security verification is loading. Please try again.');
            return;
        }

        const correctCount = getCorrectCount();
        const points = calculatePoints();

        setIsLoading(true);

        try {
            // Generate reCAPTCHA token
            const recaptchaToken = await executeRecaptchaAction('quiz_submit');

            // Submit quiz results to backend
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/quiz/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userid: userId,
                    correct_answers: correctCount,
                    total_questions: randomizedQuestions.length,
                    recaptchaToken: recaptchaToken
                })
            });

            const data = await response.json();

            if (data.success) {
                setEarnedPoints(data.data.points_earned);
                setCurrentView('complete');
                showNotification('success', `Congratulations! You earned ${data.data.points_earned} points！`);
            } else {
                // Handle error - maybe user already completed quiz
                if (data.data?.already_completed) {
                    showNotification('error', 'You have already completed this quiz!');
                    onClose();
                } else {
                    showNotification('error', data.msg || 'Submission failed, please try again');
                }
            }
        } catch (error) {
            console.error('Error submitting quiz:', error);
            showNotification('error', 'Submission failed, please try again');
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewAnswers = () => {
        setCurrentView('results');
    };

    const handleBackToWhitepaper = () => {
        onClose();
    };

    const handleShareOnX = () => {
        const score = earnedPoints;
        const tweetText = `Scored ${score.toLocaleString()} Prisma Points from the PrismaX whitepaper quiz! 🤖 Ready to learn and earn? Join the robotics revolution with me! #robotics #AI #EarnPoints app.prismax.ai/whitepaper`;
        const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
        window.open(tweetUrl, '_blank');
    };

    // Get correct answers count
    const getCorrectCount = () => {
        let count = 0;
        Object.entries(selectedAnswers).forEach(([questionIndex, selectedOption]) => {
            const question = randomizedQuestions[parseInt(questionIndex)];
            const selectedAnswer = question.options.find(opt => opt.id === selectedOption);
            if (selectedAnswer && selectedAnswer.correct) {
                count++;
            }
        });
        return count;
    };

    // Render different views
    const renderContent = () => {
        switch (currentView) {
            case 'start':
                return (
                    <div className={styles.startContent}>
                        <h2 className={styles.title}>Before You Start</h2>
                        <p className={styles.description}>
                            Read our whitepaper first - the quiz is based on it! One attempt per question. Earn <span
                            style={{color: '#FFC786', fontWeight: '600'}}>500 </span>
                            Prisma Points per correct answer, plus <span
                            style={{color: '#FFC786', fontWeight: '600'}}>1,000 </span> bonus points for answering all
                            questions
                            correctly!
                        </p>

                        <button
                            className={styles.goldButton1}
                            onClick={() => {
                                if (!userId) {
                                    showNotification('error', 'Authentication required. Please log in.');
                                    return;
                                }
                                setCurrentView('quiz');
                            }}
                            style={{backgroundImage: `url(${goldBg})`, height: "53px"}}
                        >
                            Take the quiz
                        </button>
                    </div>
                );

            case 'quiz':
                const question = randomizedQuestions[currentQuestion];
                const isLastQuestion = currentQuestion === randomizedQuestions.length - 1;
                const allAnswered = Object.keys(selectedAnswers).length === randomizedQuestions.length;

                return (
                    <div className={styles.quizContent}>
                        <div className={styles.quizHeader}>
                            <span className={styles.quizTitle}>Quiz {currentQuestion + 1}: {question.question}</span>
                        </div>

                        <div className={styles.optionsContainerOuter}>
                            {currentQuestion === 0 ? <button className={styles.navButton} style={{width: '169.54px'}}>
                                </button> :
                                <button className={styles.navButton} style={{paddingLeft: '80px'}} onClick={handleBack}>
                                    ← Back
                                </button>
                            }
                            <div className={styles.optionsContainer}>
                                {question.options.map((option) => {
                                    const isSelected = selectedAnswers[currentQuestion] === option.id;
                                    const isHovered = hoveredOption === option.id;

                                    return (
                                        <div
                                            key={option.id}
                                            className={`${styles.option} ${isSelected ? styles.selected : ''} ${isHovered ? styles.hovered : ''}`}
                                            onClick={() => handleAnswerSelect(option.id)}
                                            onMouseEnter={() => setHoveredOption(option.id)}
                                            onMouseLeave={() => setHoveredOption(null)}
                                        >
                                            <span className={styles.optionId}>{option.id}</span>
                                            <span className={styles.optionText}>{option.text}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            {isLastQuestion
                                ? (
                                    allAnswered ? (
                                        <div className={styles.goldButtonContainer}
                                             style={{display: 'flex', alignItems: 'center', width: '169.54px'}}>
                                            <button
                                                className={styles.goldButton}
                                                onClick={handleFinishQuiz}
                                                style={{backgroundImage: `url(${goldBg})`, marginLeft: '40px'}}
                                                disabled={isLoading}
                                            >
                                                {isLoading ? 'Submitting…' : 'Finish Quiz →'}
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className={styles.navButton}
                                            style={{width: '169.54px'}}
                                            disabled
                                        />
                                    )
                                )
                                : (
                                    <button
                                        className={styles.navButton}
                                        onClick={handleNext}
                                        disabled={!selectedAnswers[currentQuestion]}
                                        style={{paddingRight: '80px'}}
                                    >
                                        Next →
                                    </button>
                                )
                            }
                        </div>

                        <div className={styles.navigationContainerMobile}>
                            {currentQuestion > 0 && (
                                <button className={styles.navButtonMobile} onClick={handleBack}>
                                    ← Back
                                </button>
                            )}

                            <div className={styles.navSpacer}/>

                            {isLastQuestion && allAnswered ? (
                                <button
                                    className={styles.goldButtonMobile}
                                    onClick={handleFinishQuiz}
                                    style={{backgroundImage: `url(${goldBg})`}}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Submitting...' : 'Finish Quiz →'}
                                </button>
                            ) : !isLastQuestion && (
                                <button
                                    className={styles.navButtonMobile}
                                    onClick={handleNext}
                                    disabled={!selectedAnswers[currentQuestion]}
                                >
                                    Next →
                                </button>
                            )}
                        </div>
                    </div>
                );

            case 'complete':
                return (
                    <div className={styles.completeContent}>
                        <img className={styles.completeImg} style={{width: '36px', height: '44px'}} src={pointIcon}
                             alt=''/>
                        <h2 className={styles.congratsTitle}>Congratulations!</h2>
                        <p className={styles.pointsText}>
                            You earned a total of <span className={styles.pointsHighlight}>+{earnedPoints}</span> Prisma
                            Points by completing the quiz.
                        </p>
                        <div className={styles.completeButtons}>
                            <button className={styles.secondaryButton} onClick={handleBackToWhitepaper}>
                                Back to Whitepaper
                            </button>
                            <button
                                className={styles.goldButton1}
                                onClick={handleViewAnswers}
                                style={{backgroundImage: `url(${goldBg})`, height: '53px'}}
                            >
                                View your answers
                            </button>
                        </div>
                    </div>
                );

            case 'results':
                const correctCount = !isReviewMode ? getCorrectCount() : 0;

                return (
                    <div className={styles.resultsContent}>
                        <h2 className={styles.resultsTitle}>
                            {isReviewMode ? 'Quiz Answers' : `${correctCount}/5 Correct Answers`}
                        </h2>

                        <div className={styles.resultsContainer}>
                            {randomizedQuestions.map((question, index) => {
                                const selectedOption = selectedAnswers[index];
                                const selectedAnswer = question.options.find(opt => opt.id === selectedOption);
                                const isCorrect = selectedAnswer && selectedAnswer.correct;
                                const correctOption = question.options.find(opt => opt.correct);

                                return (
                                    <div key={index} className={styles.resultItem}>
                                        <div className={styles.resultHeader}>
                                            <span className={styles.resultNumber}>Q{index + 1}</span>
                                            <span className={styles.resultQuestion}>{question.question}</span>
                                            
                                            {!isReviewMode && (
                                                isCorrect ?
                                                    <img src={checkCorrectIcon} alt='✓'/> :
                                                    <img src={checkWrongIcon} alt='✗'/>
                                            )}
                                        </div>
                                        <p className={styles.resultAnswer}>
                                            {correctOption?.text}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{display: 'flex', justifyContent: 'center'}}>
                            <button
                                className={styles.goldButton1}
                                onClick={handleShareOnX}
                                style={{backgroundImage: `url(${goldBg})`, width: '210px'}}

                            >
                                Share on X
                            </button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // Show progress indicator for quiz view
    const showProgress = currentView === 'quiz';

    return ReactDOM.createPortal(
        <div className={styles.modalOverlay}
             onClick={(e) => {
                 if (e.target === e.currentTarget) {
                     onClose();
                 }
             }}>

            {/* Confetti canvas - only visible when points >= 500 in complete view */}
            {currentView === 'complete' && earnedPoints >= 500 && (
                <canvas
                    ref={canvasRef}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 1002
                    }}
                />
            )}

            <div className={styles.modalContainer} ref={modalContainerRef}>
                <div className={styles.headerRow} style={{display: 'flex', justifyContent: 'space-between'}}>
                    {/* Show points earned in header during quiz */}
                    {(showProgress || currentView === 'start') && (
                        <div className={styles.pointsIndicator}>
                            <img src={pointIcon} alt='🛡' style={{width: '17px', height: '20px'}}/>
                            <span>0 </span> <span>Prisma Points</span>
                        </div>
                    )}

                    {/* Show points earned after completion */}
                    {(currentView === 'complete' || currentView === 'results') && (
                        <div className={styles.pointsIndicator}>
                            <img src={pointIcon} alt='🛡'/>
                            <span>+{earnedPoints} Prisma Points</span>
                        </div>
                    )}

                    <img onMouseEnter={() => setCloseButtonHover(true)}
                         onMouseLeave={() => setCloseButtonHover(false)}
                         src={closeButtonHover ? closeButtonHoverImg : closeButtonImg} className={styles.closeBtn}
                         onClick={onClose} alt='x'/>
                </div>


                {renderContent()}
            </div>
        </div>,
        document.getElementById('root')
    );
}

export default QuizModal;